import axios from "axios";
import { BASE_URL } from "./proApi";
import { loadRazorpay } from "./proSystemApi";
import { RAZORPAY_BRAND } from "../utils/razorpayBranding";

// =====================================================
// BUY ME A TREAT — CLIENT API
//
// Nothing here decides anything. Treat prices come from the server's
// database, the order amount is set by the server, and a treat counts as paid
// only when the server says so — never on Razorpay's callback alone.
// =====================================================

function authConfig() {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
}

const get = async (path) => (await axios.get(`${BASE_URL}${path}`, authConfig())).data;
const post = async (path, body) =>
  (await axios.post(`${BASE_URL}${path}`, body || {}, authConfig())).data;
const put = async (path, body) =>
  (await axios.put(`${BASE_URL}${path}`, body || {}, authConfig())).data;

const query = (params = {}) => {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
  return q ? `?${q}` : "";
};

// Treat images are either absolute (admin uploads) or served from /treats/.
export const treatImageSrc = (image) => image || "";

// ── READER ──
export const getTreatCatalog = () => get("/api/treats/catalog");
export const getTreatBlog = (blogId) => get(`/api/treats/blog/${blogId}`);
export const getSentTreats = () => get("/api/treats/sent");
export const getSentTreat = (treatId) => get(`/api/treats/sent/${treatId}`);

// ── BLOGGER ──
export const getReceivedTreats = () => get("/api/treats/received");
export const reportTreatGreeting = (treatId, reason, details) =>
  post(`/api/treats/${treatId}/report`, { reason, details });
export const sendTreatThanks = (treatId, message) => post(`/api/treats/${treatId}/thanks`, { message });
export const getPayoutSetup = () => get("/api/treats/payout-setup");
export const getPayoutAccount = () => get("/api/treats/payout-account");
export const savePayoutAccount = (details) => put("/api/treats/payout-account", details);

// ── ADMIN ──
export const adminTreatTransactions = (filters) => get(`/api/admin/treats/transactions${query(filters)}`);
export const adminTreatItems = () => get("/api/admin/treats/items");
export const adminCreateTreatItem = (formData) => post("/api/admin/treats/items", formData);
export const adminUpdateTreatItem = (id, formData) => put(`/api/admin/treats/items/${id}`, formData);
export const adminPayoutAccounts = (status) => get(`/api/admin/treats/payout-accounts${query({ status })}`);
export const adminRevealAccountNumber = (id) => get(`/api/admin/treats/payout-accounts/${id}/account-number`);
export const adminVerifyPayoutAccount = (id) => post(`/api/admin/treats/payout-accounts/${id}/verify`);
export const adminRejectPayoutAccount = (id, reason) =>
  post(`/api/admin/treats/payout-accounts/${id}/reject`, { reason });
export const adminBloggerPayouts = () => get("/api/admin/treats/payouts/bloggers");
export const adminStartPayout = (bloggerId, note) => post("/api/admin/treats/payouts", { bloggerId, note });
export const adminMarkPayoutPaid = (payoutId, reference, note) =>
  post(`/api/admin/treats/payouts/${payoutId}/paid`, { reference, note });
export const adminMarkPayoutFailed = (payoutId, note) =>
  post(`/api/admin/treats/payouts/${payoutId}/failed`, { note });
export const adminTreatReports = (status) => get(`/api/admin/treats/reports${query({ status })}`);
export const adminDismissTreatReport = (reportId, note) =>
  post(`/api/admin/treats/reports/${reportId}/dismiss`, { note });
export const adminBlockTreatSender = (reportId, note) =>
  post(`/api/admin/treats/reports/${reportId}/block`, { note });

// =====================================================
// PAYMENT — same three steps as the Pro ₹19 checkout: server order →
// Razorpay → server verification. `onSuccess` fires on the SERVER's answer.
// =====================================================
export async function payForTreat({ blogId, treatId, message, userName, onSuccess, onError, onClose }) {
  const ready = await loadRazorpay();

  if (!ready) {
    onError?.("Payment is still loading. Please try again in a moment.");
    return;
  }

  let order;

  try {
    order = await post("/api/treats/create-order", { blogId, treatId, message });
  } catch (err) {
    onError?.(err?.response?.data?.message || "Unable to start the payment");
    return;
  }

  const checkout = new window.Razorpay({
    key: order.key_id,
    amount: order.order.amount,
    currency: order.order.currency,
    ...RAZORPAY_BRAND,
    description: `${order.treat?.name || "Treat"} for ${order.blogger?.name || "the blogger"}`,
    order_id: order.order.id,

    handler: async (response) => {
      try {
        const verified = await post("/api/treats/verify", {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });

        if (verified?.success) onSuccess?.({ ...verified, treatId: verified.treatId || order.treatId });
        else onError?.(verified?.message || "Payment could not be verified");
      } catch (err) {
        onError?.(
          err?.response?.data?.message ||
            "Payment verification failed. If money was deducted, it will show in My Sent Treats once confirmed."
        );
      }
    },

    prefill: { name: userName || "" },
    theme: { color: "#8b5cf6" },
    modal: { ondismiss: () => onClose?.() },
  });

  checkout.on("payment.failed", (response) => {
    const reason = response?.error?.description || "Payment failed. Please try again.";
    // Recorded for the order's history only; it can never mark a paid treat failed.
    post("/api/treats/failed", { razorpay_order_id: order.order.id, reason }).catch(() => {});
    onError?.(reason);
  });

  checkout.open();
}
